import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { TableAsyncPrintProps } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Document, Page, View, Text } from "@react-pdf/renderer";
import TableAsyncPrintRow from "./tableAsyncPrintRow";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import tableAsyncStyles from "theme/custom/tableAsyncPdf";
import useTableHelper from "../useTableHelper";

const TableAsyncPrint = <RowType extends Record<string, any>>(
  props: React.PropsWithChildren<TableAsyncPrintProps<RowType>>
) => {
  const { title, subTitle, reportDateTime, columns, rows, pdfHeaderTextSmall } = props;
  const { getTableColumnTitle } = useTableHelper();

  const pdfMaxWidth = 810;

  const [totalSpecifiedColumnWidth, totalSpecifiedColumnCount] = columns.reduce(
    ([totalSpecifiedColumnWidth, widthSpecifiedColumCount], [key, cellValue]) => {
      return [
        totalSpecifiedColumnWidth + (cellValue?.pdfColumnwidth ?? 0),
        widthSpecifiedColumCount + (cellValue?.pdfColumnwidth ? 1 : 0),
      ];
    },
    [0, 0]
  );

  const nonSpecifiedColumnWidth =
    (pdfMaxWidth - totalSpecifiedColumnWidth) / (columns.length - totalSpecifiedColumnCount);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={tableAsyncStyles.page}>
        <View>
          <Text style={tableAsyncStyles.printDate}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PrintDate, undefined, {
              date: reportDateTime,
            })}
          </Text>
        </View>
        <View style={tableAsyncStyles.titleContainer}>
          <Text style={tableAsyncStyles.title}>{title}</Text>
          <Text style={tableAsyncStyles.subTitle}>{subTitle}</Text>
        </View>
        <View style={tableAsyncStyles.table}>
          <View fixed style={[tableAsyncStyles.row, tableAsyncStyles.tableHead]}>
            {columns.map(([headerKey, value], index) => (
              <View
                key={`tab-header-${index}-${headerKey}`}
                style={[
                  tableAsyncStyles.tableHeaderCell,
                  tableAsyncStyles.truncateString,
                  value?.isTextSmall || pdfHeaderTextSmall ? tableAsyncStyles.textSmall : tableAsyncStyles.textNormal,
                  index === 0
                    ? tableAsyncStyles.firstHeaderCell
                    : index === columns.length - 1
                    ? tableAsyncStyles.lastHeaderCell
                    : tableAsyncStyles.empty,
                  {
                    width: value?.pdfColumnwidth ?? nonSpecifiedColumnWidth,
                    textAlign:
                      value?.alignment === undefined || value?.alignment === "inherit" ? "left" : value?.alignment,
                    flexWrap: value?.noWrap ? "nowrap" : "wrap",
                  },
                ]}
              >
                <Text style={tableAsyncStyles.rowMainText}>
                  {value?.description && getTableColumnTitle(value.description)}
                </Text>
              </View>
            ))}
          </View>
          {rows.map((row, index, array) => (
            <TableAsyncPrintRow
              key={`tab-row-${index}`}
              row={row}
              index={index}
              columns={columns}
              nonSpecifiedColumnWidth={nonSpecifiedColumnWidth}
            />
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default TableAsyncPrint;
